import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import { copyProductsIntoNewTimeframe } from '@adornis/buchungsheld-db/api/product.js';
import type { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, filter, firstValueFrom, map, switchMap, timer } from 'rxjs';

@customElement('bh-prompt-timeframe-sync')
export class BHPromptTimeframeSync extends TrDialog<void> {
  @property({ attribute: false }) timeframe = new RXController<Maybe<Timeframe>>(this, undefined);
  @property() private readonly institutionID = new RXController<string | null>(this, null);

  @state() private readonly _productsByTimeframe = new RXController(
    this,
    this.timeframe.observable.pipe(
      filter(Boolean),
      map(timeframe => timeframe._id),
      switchMap(timeframeID => Workshop.getAllByTimeframe(timeframeID)({ productCopyID: 1 })),
      map(workshops => workshops.map(workshop => workshop.productCopyID)),
    ),
  );
  @state() private readonly _otherTimeframes = new RXController(
    this,
    combineLatest([
      this.timeframe.observable.pipe(
        filter(Boolean),
        map(timeframe => timeframe._id),
      ),
      this.institutionID.observable,
    ]).pipe(
      switchMap(([timeframeID, institutionID]) =>
        Timeframe.subscribeTimeframesByInstitution(institutionID, timeframeID)(Timeframe.allFields),
      ),
    ),
  );
  @state() private readonly _selectedTimeframe = new RXController<Maybe<Timeframe>>(this, undefined);
  @state() private readonly _productsByOtherTimeframe = new RXController(
    this,
    this._selectedTimeframe.observable.pipe(
      filter(Boolean),
      map(timeframe => timeframe._id),
      switchMap(timeframeID => Workshop.getAllByTimeframe(timeframeID)({ _id: 1, name: 1 })),
    ),
  );
  @state() private readonly _syncedWorkshops = new RXController(
    this,
    this._productsByOtherTimeframe.observable.pipe(
      filter(Boolean),
      map(products => products.filter(workshop => this._productsByTimeframe.value!.includes(workshop._id))),
    ),
  );
  @state() private _save = false;
  private readonly _selectedProducts = new Map<string, Product>();

  override render() {
    if (!this.timeframe.value || !this._otherTimeframes.value) {
      return html` <x-loading-state no-wrapper></x-loading-state> `;
    }

    if (this._save) {
      return html`
        <x-flex space="md">
          <tr-h3 bold>${this._translation.translate('PROMPT', 'complete_sync')}</tr-h3>

          <x-text> ${this._translation.translate('PROMPT', 'confirm_sync')} </x-text>

          <x-hr></x-hr>

          <tr-h4> ${this._selectedTimeframe.value?.name} </tr-h4>

          <x-flex space="sm">
            ${Array.from(this._selectedProducts.values()).map(product => this._stateSynced(product))}
          </x-flex>

          <x-flex horizontal space-between crossaxis-center space="md" wrap>
            <x-button mode="outline" @click=${() => (this._save = false)}
              >${this._translation.translate('PROMPT', 'edit_selection')}</x-button
            >
            <x-button
              @click=${async () => {
                const syncJob = async () => {
                  if (!this.timeframe.value) return;
                  const productIDs = Array.from(this._selectedProducts.values()).map(product => product._id);
                  await copyProductsIntoNewTimeframe(productIDs, this.timeframe.value._id)();

                  this.close();
                };

                return XDialog.waitFor(
                  Promise.all([syncJob(), firstValueFrom(timer(1500))]),
                  this._translation.translate('syncing') as string,
                );
              }}
            >
              ${this._translation.translate('sync')}
            </x-button>
          </x-flex>
        </x-flex>
      `;
    }

    return html`
      <x-flex space="md">
        <tr-h3 bold>${this._translation.translate('PROMPT', 'exec_sync')}</tr-h3>

        <tr-h4>${this._translation.translate('PROMPT', 'choose_timeframe')}</tr-h4>
        <tr-dropdown-selection
          .value=${this._selectedTimeframe.value}
          .selectables=${this._otherTimeframes.value}
          .renderString=${(timeframe: Timeframe) => timeframe.name}
          @value-picked=${(e: ValueEvent<Timeframe>) => this._selectedTimeframe.next(e.detail.value)}
        ></tr-dropdown-selection>

        ${this._renderWorkshops()}

        <x-flex horizontal space-between crossaxis-center space="md" wrap>
          <x-button mode="outline" @click=${() => this.cancel()}>${this._translation.translate('discard')}</x-button>
          <x-button
            @click=${() => {
              this._save = true;
            }}
          >
            ${this._translation.translate('finish')}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _renderWorkshops() {
    if (!this._selectedTimeframe.value) return nothing;
    if (
      this._productsByOtherTimeframe.isLoading ||
      !this._productsByTimeframe.value ||
      this._syncedWorkshops.isLoading ||
      !this._syncedWorkshops.value
    ) {
      return html` <tr-loader></tr-loader> `;
    }
    if (!this._productsByOtherTimeframe.value || this._productsByOtherTimeframe.value.length === 0)
      return html`
        <tr-text tone="error"> ${this._translation.translate('PROMPT', 'no_workshops_for_timeframe')} </tr-text>
      `;

    const selectedWorkshops = this._productsByOtherTimeframe.value.filter(workshop =>
      Array.from(this._selectedProducts.keys()).includes(workshop._id),
    );

    const clickableWorkshops = this._productsByOtherTimeframe.value.filter(workshop => {
      if (this._syncedWorkshops.value?.find(filterWorkshop => filterWorkshop._id === workshop._id)) return false;
      return true;
    });
    const selectedIDs = Array.from(this._selectedProducts.keys());
    const hasWorkshopsSelectable = clickableWorkshops.length > 0;
    const isEveryWorkshopSelected = clickableWorkshops.every(workshop => selectedIDs.includes(workshop._id));

    return html`
      <x-flex space="sm">
        ${this._syncedWorkshops.value.map(workshop => this._stateSynced(workshop))}
        <tr-text
          ${css({ color: this.colors.accent, alignSelf: 'end', cursor: 'pointer', userSelect: 'none' })}
          @click=${() => {
            if (isEveryWorkshopSelected) {
              clickableWorkshops.forEach(workshop => this._selectedProducts.delete(workshop._id));
            } else {
              clickableWorkshops.forEach(workshop => this._selectedProducts.set(workshop._id, workshop));
            }
            this.requestUpdate();
          }}
        >
          ${hasWorkshopsSelectable
            ? isEveryWorkshopSelected
              ? this._translation.translate('PROMPT', 'select_none')
              : this._translation.translate('PROMPT', 'select_all')
            : nothing}
        </tr-text>
        ${this._productsByOtherTimeframe.value.map(workshop => {
          if (this._syncedWorkshops.value?.find(filterWorkshop => filterWorkshop._id === workshop._id)) return nothing;
          if (selectedWorkshops.find(filterWorkshop => filterWorkshop._id === workshop._id))
            return this._stateSelected(workshop);
          return this._stateDefault(workshop);
        })}
      </x-flex>
    `;
  }

  // workshop states

  // selected
  private _stateSynced(product: Product) {
    return html`
      <tr-flex horizontal crossaxis-center synced space="md" state>
        <tr-text flex> ${product.name} </tr-text>
        <x-icon justify-end> check </x-icon>
      </tr-flex>
    `;
  }

  // synced
  private _stateSelected(product: Product) {
    return html`
      <tr-flex
        horizontal
        crossaxis-center
        selected
        space="md"
        state
        clickable
        @click=${() => {
          this._selectedProducts.delete(product._id);
          this.requestUpdate();
        }}
      >
        <tr-text flex> ${product.name} </tr-text>
        <x-icon justify-end> sync </x-icon>
      </tr-flex>
    `;
  }

  // default
  private _stateDefault(product: Product) {
    return html`
      <tr-flex
        horizontal
        crossaxis-center
        default
        space="md"
        state
        clickable
        @click=${() => {
          this._selectedProducts.set(product._id, product);
          this.requestUpdate();
        }}
      >
        <tr-text flex> ${product.name} </tr-text>
        <x-icon justify-end> sync </x-icon>
      </tr-flex>
    `;
  }

  static sync(institutionID: string, timeframe: Timeframe) {
    return this.showPopup({ props: { timeframe, institutionID } });
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close();
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  static override get element_name() {
    return 'bh-prompt-timeframe-sync';
  }

  override styles() {
    return [
      ...super.styles(),
      {
        // selected
        'tr-flex[selected]': {
          background: this.colors.accent,
          borderRadius: this.sizes.borderRadius,
          color: '#fff',
        },
        'tr-flex[selected]:before': {
          content: "''",
          borderRadius: '50%',
          height: '10px',
          width: '10px',
          background: '#fff',
        },
        // synced
        'tr-flex[synced]': {
          background: this.colors.accent.shade(50),
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        // default
        'tr-flex[default]': {
          background: this.colors.tone.lightGrey,
          borderRadius: this.sizes.borderRadius,
          color: '#000',
        },
        'tr-flex[default]:before': {
          content: "''",
          borderRadius: '50%',
          height: '6px',
          width: '6px',
          border: `2px solid ${this.colors.accent}`,
        },
        'tr-flex[state]': {
          padding: `${this.spacing.xs} ${this.spacing.md}`,
          userSelect: 'none',
        },
        'tr-flex[clickable]': {
          cursor: 'pointer',
        },
      },
    ];
  }
}
